import React, { useState, useEffect } from 'react';
import treatmentService from '../../services/treatmentService';

const PatientTreatments = ({ patientId, onAddTreatment }) => {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  useEffect(() => {
    if (patientId) {
      fetchTreatments();
    }
  }, [patientId]);

  const fetchTreatments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await treatmentService.getPatientTreatments(patientId);
      setTreatments(response.data.treatments || []);
    } catch (err) {
      setError('Failed to load treatments');
      console.error('Error fetching treatments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (treatmentId) => {
    if (!window.confirm('Are you sure you want to delete this treatment record?')) {
      return;
    }

    try {
      await treatmentService.deleteTreatment(treatmentId);
      setTreatments(treatments.filter(t => t.id !== treatmentId));
    } catch (err) {
      alert('Failed to delete treatment');
      console.error('Error deleting treatment:', err);
    }
  };

  const getPainLevelColor = (level) => {
    if (level <= 3) return 'text-green-600 bg-green-50';
    if (level <= 6) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!patientId) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl text-gray-400">💊</div>
        <p className="mt-2 text-gray-500">No patient selected</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p className="mt-2 text-gray-600">Loading treatments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchTreatments}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Treatment History</h3>
          <p className="text-sm text-gray-600 mt-1">
            {treatments.length} session{treatments.length !== 1 ? 's' : ''} recorded
          </p>
        </div>
        <button
          onClick={() => onAddTreatment && onAddTreatment()}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          <span className="text-lg">➕</span>
          Add Treatment
        </button>
      </div>

      {treatments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-6xl text-gray-400">📄</div>
          <p className="mt-2 text-gray-600 font-medium">No treatment records yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Start documenting patient treatments by adding a new record
          </p>
          <button
            onClick={() => onAddTreatment && onAddTreatment()}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Add First Treatment
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {treatments.map((treatment) => (
            <div
              key={treatment.id}
              className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <span className="text-2xl">📅</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {formatDate(treatment.session_date)}
                    </p>
                    <p className="text-sm text-gray-500">
                      Session #{treatment.id}
                    </p>
                  </div>
                </div>

                {treatment.pain_level && (
                  <div className={`px-3 py-1 rounded-full font-semibold text-sm ${getPainLevelColor(treatment.pain_level)}`}>
                    Pain: {treatment.pain_level}/10
                  </div>
                )}
              </div>

              {/* Treatment Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Treatment Performed</p>
                  <p className="text-gray-900">{treatment.treatment_performed}</p>
                </div>

                {treatment.observations && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Observations</p>
                    <p className="text-gray-600">{treatment.observations}</p>
                  </div>
                )}

                {treatment.prescribed_exercises && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Prescribed Exercises</p>
                    <p className="text-gray-600">{treatment.prescribed_exercises}</p>
                  </div>
                )}

                {treatment.improvement_notes && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Improvement Notes</p>
                    <p className="text-green-600">{treatment.improvement_notes}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setSelectedTreatment(treatment)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                >
                  <span>✏️</span>
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(treatment.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <span>🗑️</span>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Treatment Detail Modal */}
      {selectedTreatment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Treatment Details</h3>
              <button
                onClick={() => setSelectedTreatment(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Treatment ID: {selectedTreatment.id}</p>
              <p className="text-gray-600">Date: {formatDate(selectedTreatment.session_date)}</p>
              {/* Add more details or edit form here */}
              <button
                onClick={() => setSelectedTreatment(null)}
                className="mt-6 w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientTreatments;